import { Middleware } from "redux"
import { RootRouteContentType } from "@fusion-advantage/formlib"
import { AppDispatch, AppRootState, AppThunk } from "./appStore"
import { selectDashboardDisplayIdentity } from "./dashboardDataSlice"
import { setBackgroundSyncTimeoutId } from "./backgroundSlice"
import { appSync } from "./syncthunk/syncThunk"
import { SyncType } from "./syncthunk/SyncConfig"
import { pushLogEntry } from "./logSlice"
import { selectIsPreviewMode } from "./selectPreview"

// attempt background sync of pending items every 3 minutes
const BACKGROUND_SYNC_INTERVAL = 60000 * 3

export const backgroundSyncMw: Middleware<{}, AppRootState> = store => {
	const dispatch = store.dispatch as AppDispatch
	return next => action => {
		const prevState = store.getState()
		const prevIdentity = selectDashboardDisplayIdentity(prevState)
		const prevContentType = prevState.app.content.type
		const result = next(action)
		const nextState = store.getState()
		const nextIdentity = selectDashboardDisplayIdentity(nextState)
		const nextContentType = nextState.app.content.type
		// branch preview syncs once on load and never again: background
		// syncs would download the user's work items into the preview
		if(selectIsPreviewMode(nextState)) {
			return result
		}
		if(prevIdentity === null && nextIdentity !== null) {
			dispatch(scheduleFutureBackgroundSync())
		}
		else if(prevIdentity !== null && nextIdentity === null) {
			dispatch(cancelFutureBackgroundSync())
		}
		// when the user returns to the dashboard (from a form or elsewhere),
		// kick off a silent, non-blocking background sync so freshly captured
		// data is pushed without waiting up to 3 minutes for the next tick.
		// DashboardBackground shows no modal and suppresses error alerts, so
		// this never hangs the tool on low connectivity; the appSync
		// single-flight guard drops it harmlessly if a sync is already running.
		if(
			nextIdentity !== null &&
			prevContentType !== RootRouteContentType.Dashboard &&
			nextContentType === RootRouteContentType.Dashboard
		) {
			dispatch(backgroundSyncMwTick({reschedule: false}))
		}
		return result
	}
}

interface backgroundSyncMwTickArgs {
	reschedule: boolean
}

export const backgroundSyncMwTick = ({reschedule}: backgroundSyncMwTickArgs): AppThunk<Promise<void>> => async (dispatch, getState) => {
	dispatch(pushLogEntry(`[backgroundSyncMw] backgroundSyncMwTick`))
	if(selectIsPreviewMode(getState())) {
		// do not reschedule
		return
	}
	// performing this type of sync also checks
	// if the cookie/token session is expired
	await dispatch(appSync({
		type: SyncType.DashboardBackground,
	}))
	if(reschedule) {
		dispatch(scheduleFutureBackgroundSync())
	}
}

const scheduleFutureBackgroundSync = (): AppThunk<void> => (dispatch) => {
	dispatch(pushLogEntry(`[backgroundSyncMw] scheduleFutureBackgroundSync`))
	const timeoutId = setTimeout(() => dispatch(backgroundSyncMwTick({reschedule: true})), BACKGROUND_SYNC_INTERVAL)
	dispatch(setBackgroundSyncTimeoutId(timeoutId))
}

const cancelFutureBackgroundSync = (): AppThunk<void> => (dispatch, getState) => {
	dispatch(pushLogEntry(`[backgroundSyncMw] cancelFutureBackgroundSync`))
	const timeoutId = getState().background.syncTimeoutId
	if(timeoutId) {
		clearTimeout(timeoutId)
		dispatch(setBackgroundSyncTimeoutId(null))
	}
}
